<template>
  <transition name="slide-out">
    <div v-if="slideOutOpen" class="viq-slideout">
      <div class="viq-slideout__overlay" @click="close" />
      <div class="viq-slideout__panel">
        <div class="viq-slideout__header">
          <h3 class="viq-slideout__title">Source Article</h3>
          <button class="viq-slideout__close" @click="close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <div class="viq-slideout__body">
          <div v-if="loading" class="viq-slideout__loading">
            <div class="viq-slideout__skeleton" v-for="i in 8" :key="i" />
          </div>
          <ViqArticleDetail
            v-else-if="slideOutArticle"
            :article="slideOutArticle"
          />
          <div v-else class="viq-slideout__empty">
            No article selected
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ViqSlideOut",
  components: {
    ViqArticleDetail: () => import("./ViqArticleDetail"),
  },
  computed: {
    ...mapState("Viq", ["slideOutOpen", "slideOutArticle"]),
  },
  data() {
    return {
      loading: false,
    };
  },
  watch: {
    slideOutOpen(val) {
      const hasExcerpts = this.slideOutArticle && (this.slideOutArticle.excerpt || (this.slideOutArticle.excerpts && this.slideOutArticle.excerpts.length));
      if (val && this.slideOutArticle && this.slideOutArticle.id && !this.slideOutArticle.fullLoaded && !hasExcerpts) {
        this.loadArticle(this.slideOutArticle.id);
      }
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleEscape);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEscape);
  },
  methods: {
    close() {
      this.$store.dispatch("Viq/closeSlideOut");
    },
    handleEscape(e) {
      if (e.key === "Escape" && this.slideOutOpen) {
        this.close();
      }
    },
    async loadArticle(id) {
      this.loading = true;
      try {
        const article = await this.$store.dispatch("Viq/fetchArticle", id);
        if (article) {
          this.$store.commit("Viq/SET_SLIDE_OUT", {
            open: true,
            article: {
              ...this.slideOutArticle,
              ...article,
              content: article.content_html || article.content || this.slideOutArticle.content,
              fullLoaded: true,
            },
          });
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-slideout {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  justify-content: flex-end;

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
  }

  &__panel {
    position: relative;
    width: $viq-slideout-width;
    max-width: 90vw;
    background: $viq-bg-primary;
    box-shadow: $viq-shadow-lg;
    display: flex;
    flex-direction: column;
    z-index: 1;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $viq-space-md $viq-space-lg;
    border-bottom: 1px solid $viq-border;
  }

  &__title {
    font-size: $viq-font-md;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: none;
    color: $viq-text-secondary;
    cursor: pointer;
    border-radius: $viq-radius-sm;
    transition: background $viq-transition-fast;

    &:hover {
      background: $viq-bg-secondary;
    }
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: $viq-space-lg;
  }

  &__empty {
    text-align: center;
    padding: $viq-space-xl;
    color: $viq-text-secondary;
    font-size: $viq-font-base;
  }

  &__loading {
    padding: $viq-space-md 0;
  }

  &__skeleton {
    height: 16px;
    background: $viq-bg-secondary;
    border-radius: $viq-radius-sm;
    margin-bottom: $viq-space-sm;
    animation: slideout-pulse 1.5s ease-in-out infinite;

    &:nth-child(1) { width: 40%; height: 12px; }
    &:nth-child(2) { width: 85%; height: 24px; margin-bottom: $viq-space-lg; }
    &:nth-child(3) { width: 100%; }
    &:nth-child(4) { width: 95%; }
    &:nth-child(5) { width: 100%; }
    &:nth-child(6) { width: 90%; }
    &:nth-child(7) { width: 100%; }
    &:nth-child(8) { width: 60%; }
  }

  @keyframes slideout-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
}

.slide-out-enter-active,
.slide-out-leave-active {
  transition: opacity 0.25s ease;

  .viq-slideout__panel {
    transition: transform 0.25s ease;
  }
}

.slide-out-enter,
.slide-out-leave-to {
  opacity: 0;

  .viq-slideout__panel {
    transform: translateX(100%);
  }
}
</style>
